import { useMemo, useState, useEffect, useCallback } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { format, startOfMonth, startOfToday } from "date-fns";
import { th } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { OccurrenceRow } from "@/components/teacher/schedule/calendar/OccurrenceRow";
import type { WeeklyItem, ClassTemplateEvent } from "@/types/schedule";
import { useTeacherProjectedSchedule } from "@/hooks/useTeacherProjectedSchedule";

interface TeacherScheduleCalendarProps { teacherCode?: string }

export function TeacherScheduleCalendar({ teacherCode }: TeacherScheduleCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday());
  const [viewMonth, setViewMonth] = useState<Date>(startOfMonth(startOfToday()));
  const [events, setEvents] = useState<ClassTemplateEvent[]>([]);

  const monthStart = startOfMonth(viewMonth);
  const monthStartISO = format(monthStart, 'yyyy-MM-01');
  const monthEndISO = useMemo(() => {
    const endDate = new Date(monthStart);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    return format(endDate, 'yyyy-MM-dd');
  }, [monthStart]);
  const projectedQuery = useTeacherProjectedSchedule(teacherCode, monthStartISO, monthEndISO);
  const monthlyItems = useMemo(() => (projectedQuery.data?.items ?? []) as WeeklyItem[], [projectedQuery.data]);

  const fetchEvents = useCallback(async () => {
    if (!teacherCode) return;
    const endDate = new Date(monthStart);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(0);
    const { data } = await supabase
      .from('v_class_template_events')
      .select('*')
      .eq('requested_by', teacherCode)
      .gte('event_date', monthStartISO)
      .lte('event_date', format(endDate, 'yyyy-MM-dd'));
    setEvents((data ?? []) as ClassTemplateEvent[]);
  }, [teacherCode, monthStart, monthStartISO]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const monthlyByDate = useMemo(() => {
    const map: Record<string, WeeklyItem[]> = {};
    for (const item of monthlyItems) {
      const key = item.date;
      map[key] = map[key] || [];
      map[key].push(item);
    }
    return map;
  }, [monthlyItems]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Card className="col-span-1">
        <CardContent className="pt-6">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            onMonthChange={(date) => date && setViewMonth(date)}
            locale={th}
            className="rounded-md border"
            modifiers={{
              hasCourse: (date) => {
                const dateKey = format(date, 'yyyy-MM-dd');
                return !!monthlyByDate[dateKey] && monthlyByDate[dateKey].length > 0;
              }
            }}
            modifiersClassNames={{
              selected: "bg-primary text-primary-foreground",
              hasCourse: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-primary"
            }}
          />
        </CardContent>
      </Card>

      <Card className="col-span-1 md:col-span-2">
        <CardContent className="pt-6 space-y-3">
          <div className="font-semibold">ตารางสอนของวันที่ {format(selectedDate,'yyyy-MM-dd')}</div>
          {(() => {
            const dateKey = format(selectedDate, 'yyyy-MM-dd');
            const items = monthlyByDate[dateKey] || [];
            if (!teacherCode) {
              return <div className="text-sm text-muted-foreground">กรุณากรอกรหัสครูในข้อมูลส่วนตัวเพื่อใช้งานส่วนนี้</div>;
            }
            if (projectedQuery.isLoading) {
              return <div className="text-sm text-muted-foreground">กำลังโหลด...</div>;
            }
            if (projectedQuery.isError) {
              return <div className="text-sm text-red-600">โหลดข้อมูลไม่สำเร็จ</div>;
            }
            if (items.length === 0) {
              return <div className="text-sm text-muted-foreground">ไม่มีคาบตามตารางสัปดาห์</div>;
            }
            return (
              <div className="space-y-2">
                {items.sort((a,b)=>a.start_time.localeCompare(b.start_time)).map(item => (
                  <OccurrenceRow
                    key={`${item.fixed_job_id}-${item.date}-${item.occurrence_id ?? "proj"}`}
                    item={item}
                    teacherCode={teacherCode}
                    events={events}
                    onRefresh={fetchEvents}
                  />
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
